import React, { useContext, useEffect, useRef } from "react";
import { useRouter } from "next/router";

import { LocationSearchSource } from "@vahak/core/dist/_services/city-search.service";
import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import { compareString } from "@vahak/core-ui/dist/methods/string";
import { AppContext } from "@vahak/core/dist/app-context";

import PageMetaTags from "../../../common/page-meta-tags/PageMetaTags";
import AppDownloadBannerV2 from "../../../homepage-v2/app-download-banner/AppDownloadBannerV2";
import { useWebV2Tracker } from "../../../homepage-v2/analytics/useWebV2Tracker";
import { getLorryTypeFromParam } from "../../../marketplace-new/marketplacePageHelpers";
import { IMarketplaceConfig, MARKETPLACE_ACTIONS, MarketplaceContext } from "../../../../MarketplaceContextProvider";
import { usePlaceSearchMutation } from "../../../../services/usePlaceSearchQuery";
import {
    buildMarketplaceV3SearchUrl,
    getMarketplaceV3UrlKey,
    MarketplaceV3Props,
    parseMarketplaceLocationFromParamSegment
} from "./marketplaceV3.constants";
import Styles from "./MarketplaceV3.module.scss";
import MarketplaceV3HeroSection from "./sections/MarketplaceV3HeroSection";
import MarketplaceV3LoadsSection from "./sections/MarketplaceV3LoadsSection";
import MarketplaceV3BenefitsSection from "./sections/MarketplaceV3BenefitsSection";
import MarketplaceV3WhyChooseSection from "./sections/MarketplaceV3WhyChooseSection";
import MarketplaceV3FaqSection from "./sections/MarketplaceV3FaqSection";
import { useCompactLoadBidAction } from "../../../homepage-v2/available-loads-section/useCompactLoadBidAction";
import {
    findMarketplaceStateOptionFromUrlParam,
    MARKETPLACE_STATE_OPTIONS
} from "./filter-fields/marketplaceFilterStateOptions";
import { useMarketplaceV3SharedLoadBid } from "./useMarketplaceV3SharedLoadBid";
import { useMarketplaceV3PageSeo } from "./useMarketplaceV3PageSeo";
import LoadNotAvailablePopup from "../../../marketplace-new/marketplace-page-v2/misc/LoadNotAvailablePopup";

const MarketplaceV3 = ({ urlSrc, urlDest, urlFilters }: MarketplaceV3Props) => {
    const router = useRouter();
    const { companyId, isPhoneVerified } = useContext(AppContext);
    const { marketplaceConfig, dispatchMarketplace, setShouldExecuteApi } = useContext(MarketplaceContext);
    const { mutateAsync: searchPlaceInfo } = usePlaceSearchMutation();
    const { trackEvent, eventData } = useWebV2Tracker({
        sourceScreen: WEB_V2_SOURCE_SCREENS.MARKETPLACE,
        pageOpenedEventName: GA4EventNames.WEB_V2.marketplace_page_opened
    });

    const initialPhoneVerifiedRef = useRef(isPhoneVerified);
    const hasHydratedFromUrlRef = useRef(false);
    const marketplaceUrlKey = getMarketplaceV3UrlKey(router.asPath);

    const updateUrl = (config: IMarketplaceConfig) => {
        const nextUrl = buildMarketplaceV3SearchUrl({
            srcState: config.srcStateIdOption,
            destStates: config.destStateIdsOption,
            lorryTypeIds: config.lorryTypeIds
        });

        // Compare without posting-form params so updateUrl does not wipe an open modal query.
        if (marketplaceUrlKey !== nextUrl) {
            router.push(nextUrl, undefined, { shallow: true, scroll: false });
        }

        setShouldExecuteApi(true);
    };

    useEffect(() => {
        setShouldExecuteApi(undefined);
        hasHydratedFromUrlRef.current = false;
    }, [marketplaceUrlKey, setShouldExecuteApi]);

    useEffect(() => {
        if (!router.isReady || hasHydratedFromUrlRef.current) {
            return;
        }

        hasHydratedFromUrlRef.current = true;

        (async () => {
            const params = router.query.params;
            const locationSegment = Array.isArray(params) ? params[1] : undefined;
            const parsedFromRouter = parseMarketplaceLocationFromParamSegment(
                typeof locationSegment === "string" ? locationSegment : undefined
            );

            const resolvedSrc = (urlSrc || parsedFromRouter.src).trim();
            const resolvedDest = (urlDest || parsedFromRouter.dest).trim();
            const lorryTypeIdsFromRouter = router.query.l
                ? getLorryTypeFromParam(router.query.l)?.map((lorryType) => lorryType.id) ?? []
                : [];
            const lorryTypeIds = urlFilters?.lorryTypeIds?.length ? urlFilters.lorryTypeIds : lorryTypeIdsFromRouter;

            let payload: IMarketplaceConfig = { ...marketplaceConfig, lorryTypeIds };

            if (resolvedSrc) {
                const srcStateIdOption =
                    findMarketplaceStateOptionFromUrlParam(resolvedSrc) ??
                    (await searchPlaceInfo({
                        query: resolvedSrc,
                        option: { allowState: true, source: LocationSearchSource.LoadMarketPlaceSearchSource }
                    }).then(({ data }) =>
                        MARKETPLACE_STATE_OPTIONS.find((state) => compareString(state.label, data?.[0]?.state!))
                    ));

                if (srcStateIdOption) {
                    payload = {
                        ...payload,
                        srcStateId: srcStateIdOption.value,
                        srcStateIdOption
                    };
                }
            }

            if (resolvedDest) {
                const destStateIdOption =
                    findMarketplaceStateOptionFromUrlParam(resolvedDest) ??
                    (await searchPlaceInfo({
                        query: resolvedDest,
                        option: { allowState: true, source: LocationSearchSource.LoadMarketPlaceSearchDestination }
                    }).then(({ data }) =>
                        MARKETPLACE_STATE_OPTIONS.find((state) => compareString(state.label, data?.[0]?.state!))
                    ));

                if (destStateIdOption) {
                    payload = {
                        ...payload,
                        destStateIds: [destStateIdOption.value],
                        destStateIdsOption: [destStateIdOption]
                    };
                }
            }

            dispatchMarketplace({ type: MARKETPLACE_ACTIONS.APPLY_FILTER, payload });
            updateUrl(payload);
        })();
    }, [router.isReady, marketplaceUrlKey]);

    useEffect(() => {
        if (isPhoneVerified !== initialPhoneVerifiedRef.current && companyId && isPhoneVerified) {
            updateUrl(marketplaceConfig);
        }
    }, [companyId, isPhoneVerified]);

    const { onClickLoadBid } = useCompactLoadBidAction({
        trackEvent,
        eventData,
        preBidEventName: GA4EventNames.WEB_V2.book_now
    });
    const { loadNotAvailablePopup, getSimilarLoad } = useMarketplaceV3SharedLoadBid();
    const { asPath, breadcrumbItems, faqList, inCityTextWithoutIndia, marketplaceSeoPath, pageMeta, shouldBeIndexed } =
        useMarketplaceV3PageSeo(urlSrc, urlDest);

    return (
        <>
            <PageMetaTags
                asPath={asPath}
                localeNeutralPath={marketplaceSeoPath}
                title={pageMeta.title}
                description={pageMeta.description}
                keywords={pageMeta.keywords}
                ogImgAltText={pageMeta.ogImgAltText}
                schema={{
                    breadcrumbItems,
                    faqList
                }}
                shouldBeIndexed={shouldBeIndexed}
            />

            <main className={Styles.main}>
                <MarketplaceV3HeroSection trackEvent={trackEvent} eventData={eventData} updateUrl={updateUrl} />

                <div className={Styles.contentStack}>
                    <MarketplaceV3LoadsSection
                        trackEvent={trackEvent}
                        eventData={eventData}
                        onBidNow={onClickLoadBid}
                        inCityTextWithoutIndia={inCityTextWithoutIndia}
                    />
                    <MarketplaceV3BenefitsSection />
                    <MarketplaceV3WhyChooseSection />
                    <MarketplaceV3FaqSection faqItems={faqList} />
                    <AppDownloadBannerV2 trackEvent={trackEvent} eventData={eventData} />
                </div>
            </main>

            <LoadNotAvailablePopup
                isOpen={loadNotAvailablePopup.isModalOpen}
                onSubmit={() => {
                    loadNotAvailablePopup.toggleModal();
                    getSimilarLoad?.();
                }}
                onToggle={loadNotAvailablePopup.toggleModal}
            />
        </>
    );
};

export default MarketplaceV3;
